"use client"

import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from "react"

export interface KeyboardShortcut {
  id: string
  keys: string
  description: string
  category: "Global" | "Navigation" | "Actions" | "Page"
  formatted?: string[]
  scope?: string // Scope for the hotkey (default: "*")
}

interface KeyboardShortcutContextValue {
  shortcuts: KeyboardShortcut[]
  registerShortcut: (shortcut: KeyboardShortcut) => () => void
  getShortcutsByCategory: (category: KeyboardShortcut["category"]) => KeyboardShortcut[]
  getShortcutsByScope: (scope: string) => KeyboardShortcut[]
}

const KeyboardShortcutContext = createContext<KeyboardShortcutContextValue | undefined>(undefined)

interface KeyboardShortcutProviderProps {
  children: React.ReactNode
}

export function KeyboardShortcutProvider({ children }: KeyboardShortcutProviderProps) {
  const [shortcutSets, setShortcutSets] = useState<Map<string, KeyboardShortcut[]>>(new Map())
  const shortcutSetIdRef = useRef(0)

  const shortcuts = useMemo(() => {
    const allShortcuts: KeyboardShortcut[] = []
    const seenIds = new Set<string>()

    shortcutSets.forEach((shortcuts) => {
      shortcuts.forEach((shortcut) => {
        if (!seenIds.has(shortcut.id)) {
          seenIds.add(shortcut.id)
          allShortcuts.push(shortcut)
        }
      })
    })
    return allShortcuts
  }, [shortcutSets])

  const registerShortcut = useCallback((shortcut: KeyboardShortcut) => {
    const id = String(++shortcutSetIdRef.current)
    setShortcutSets((prev) => {
      const next = new Map(prev)
      next.set(id, [shortcut])
      return next
    })

    return () => {
      setShortcutSets((prev) => {
        const next = new Map(prev)
        next.delete(id)
        return next
      })
    }
  }, [])

  const getShortcutsByCategory = useCallback(
    (category: KeyboardShortcut["category"]) => {
      return shortcuts.filter((shortcut) => shortcut.category === category)
    },
    [shortcuts]
  )

  const getShortcutsByScope = useCallback(
    (scope: string) => {
      return shortcuts.filter((shortcut) => shortcut.scope === scope)
    },
    [shortcuts]
  )

  const value = useMemo(
    () => ({
      shortcuts,
      registerShortcut,
      getShortcutsByCategory,
      getShortcutsByScope,
    }),
    [shortcuts, registerShortcut, getShortcutsByCategory, getShortcutsByScope]
  )

  return <KeyboardShortcutContext.Provider value={value}>{children}</KeyboardShortcutContext.Provider>
}

export function useKeyboardShortcutContext() {
  const context = useContext(KeyboardShortcutContext)
  if (!context) {
    throw new Error("useKeyboardShortcutContext must be used within a KeyboardShortcutProvider")
  }
  return context
}
